import { useState, useRef, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ScanBarcode, Camera, Package, Truck, X, CheckCircle2, AlertCircle } from 'lucide-react'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useActiveTenant } from '../contexts/tenant'

type Order = { id: string; orderNumber: string; status: string; customerName: string | null; carrier: string | null }
type Pallet = { id: string; code: string; status: string; locationId: string | null }
type Wave = { id: string; number: string; status: string }
type ScanResult =
  | { type: 'order'; order: Order; allowedActions: string[] }
  | { type: 'pallet'; pallet: Pallet; allowedActions: string[] }
  | { type: 'wave'; wave: Wave; allowedActions: string[] }
  | { type: 'unknown'; allowedActions: string[] }

const orderStatusLabel: Record<string, string> = {
  imported: 'Importado', awaiting_label: 'Aguardando etiqueta', reserved: 'Liberado',
  picking: 'Em separação', packed: 'Embalado', shipped: 'Expedido', invoiced: 'Faturado',
  divergent: 'Divergente', returned: 'Devolvido', cancelled: 'Cancelado', pending: 'Pendente',
}

export function CockpitPage() {
  const tenantId = useActiveTenant()
  const [code, setCode] = useState('')
  const [result, setResult] = useState<ScanResult | null>(null)
  const [flash, setFlash] = useState<{ ok: boolean; msg: string } | null>(null)
  const [scanning, setScanning] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const activeRef = useRef(false)

  const hasCamera = typeof window !== 'undefined' && 'BarcodeDetector' in window

  const scan = useMutation({
    mutationFn: async (c: string) => (await api.post('/expedition/scan', { tenantId, code: c })).data as ScanResult,
    onSuccess: (r) => { setResult(r); setFlash(null) },
    onError: (e: any) => setFlash({ ok: false, msg: e?.response?.data?.detail ?? 'Erro ao escanear' }),
  })

  const act = useMutation({
    mutationFn: async ({ orderId, action }: { orderId: string; action: string }) => {
      const path = action === 'markPacked' ? 'pack' : 'ship'
      return (await api.post(`/expedition/orders/${orderId}/${path}`, { tenantId })).data
    },
    onSuccess: (_d, v) => {
      setFlash({ ok: true, msg: v.action === 'markPacked' ? 'Embalado ✓' : 'Expedido ✓' })
      setResult(null)
      setCode('')
      inputRef.current?.focus()
    },
    onError: (e: any) => setFlash({ ok: false, msg: e?.response?.data?.detail ?? 'Erro na ação' }),
  })

  function submitCode(c: string) {
    const t = c.trim()
    if (!t) return
    setResult(null)
    scan.mutate(t)
  }

  function stopCamera() {
    activeRef.current = false
    setScanning(false)
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
  }

  async function startCamera() {
    if (!hasCamera) return
    try {
      const Detector = (window as any).BarcodeDetector
      const detector = new Detector({ formats: ['qr_code', 'code_128', 'ean_13', 'code_39'] })
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
      activeRef.current = true
      setScanning(true)
      const tick = async () => {
        if (!activeRef.current || !videoRef.current) return
        try {
          const codes = await detector.detect(videoRef.current)
          if (codes && codes.length) {
            const value = codes[0].rawValue as string
            stopCamera()
            setCode(value)
            submitCode(value)
            return
          }
        } catch { /* ignora frames sem leitura */ }
        if (activeRef.current) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    } catch (e: any) {
      setFlash({ ok: false, msg: 'Câmera indisponível — use a digitação' })
      stopCamera()
    }
  }

  useEffect(() => () => stopCamera(), [])

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <div className="flex items-center gap-2">
        <ScanBarcode className="h-6 w-6 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">Cockpit</h2>
      </div>

      {/* Entrada de código (leitor USB / digitação) */}
      <form
        onSubmit={e => { e.preventDefault(); submitCode(code) }}
        className="flex gap-2"
      >
        <input
          ref={inputRef}
          autoFocus
          inputMode="text"
          className="flex-1 rounded-xl border border-gray-300 px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Escaneie ou digite o código"
          value={code}
          onChange={e => setCode(e.target.value)}
        />
        <Button type="submit" loading={scan.isPending} className="h-12 px-5">OK</Button>
      </form>

      {/* Câmera (Android Chrome) */}
      {hasCamera && !scanning && (
        <Button variant="secondary" onClick={startCamera} className="w-full">
          <Camera className="h-4 w-4" /> Escanear com câmera
        </Button>
      )}
      {scanning && (
        <div className="relative overflow-hidden rounded-xl border border-gray-300 bg-black">
          <video ref={videoRef} className="w-full" muted playsInline />
          <button
            onClick={stopCamera}
            className="absolute right-2 top-2 rounded-full bg-black/60 p-2.5 text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="absolute inset-x-8 top-1/2 h-0.5 -translate-y-1/2 bg-blue-400/70" />
        </div>
      )}

      {/* Flash de feedback */}
      {flash && (
        <div className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${flash.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {flash.ok ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          {flash.msg}
        </div>
      )}

      {/* Resultado do scan */}
      {result && result.type === 'order' && (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">Pedido</p>
              <p className="text-2xl font-bold text-gray-900">{result.order.orderNumber}</p>
            </div>
            <Badge variant="info">{orderStatusLabel[result.order.status] ?? result.order.status}</Badge>
          </div>
          <div className="text-sm text-gray-600">
            <p>{result.order.customerName ?? 'Cliente não informado'}</p>
            <p className="text-gray-400">{result.order.carrier ?? 'Sem transportadora'}</p>
          </div>

          {result.allowedActions.length === 0 ? (
            <p className="text-sm text-gray-400">Nenhuma ação disponível neste estado.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {result.allowedActions.includes('markPacked') && (
                <Button
                  className="h-14 text-lg"
                  loading={act.isPending}
                  onClick={() => act.mutate({ orderId: result.order.id, action: 'markPacked' })}
                >
                  <Package className="h-5 w-5" /> Embalar
                </Button>
              )}
              {result.allowedActions.includes('markShipped') && (
                <Button
                  className="h-14 text-lg"
                  loading={act.isPending}
                  onClick={() => act.mutate({ orderId: result.order.id, action: 'markShipped' })}
                >
                  <Truck className="h-5 w-5" /> Expedir
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {result && result.type === 'pallet' && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-400">Palete</p>
          <p className="font-mono text-2xl font-bold text-gray-900">{result.pallet.code}</p>
          <p className="mt-1 text-sm text-gray-500">Status: {result.pallet.status}</p>
        </div>
      )}

      {result && result.type === 'wave' && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-400">Onda</p>
          <p className="font-mono text-2xl font-bold text-gray-900">{result.wave.number}</p>
          <p className="mt-1 text-sm text-gray-500">Status: {result.wave.status}</p>
        </div>
      )}

      {result && result.type === 'unknown' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          Código não reconhecido neste cliente.
        </div>
      )}
    </div>
  )
}
